import React from 'react';
import Iconify from 'src/components/Iconify';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
import { TAREFAS_DESIGN, getCorAvatar, getIniciais, PRIORITY_ORDER } from './Tarefas.design';

const D = TAREFAS_DESIGN;

export const FALLBACK_TASK_COLUMNS = [
    { id: 'A Fazer', label: 'A Fazer', cor: '#6b7280' },
    { id: 'Em Progresso', label: 'Em Progresso', cor: '#3b82f6' },
    { id: 'Concluído', label: 'Concluído', cor: '#22c55e' },
];

export function columnsFromWorkflow(workflow) {
    if (workflow?.estagios?.length > 0) {
        return [...workflow.estagios]
            .sort((a, b) => a.ordem - b.ordem)
            .map((e) => ({ id: e.nome, label: e.nome, cor: e.cor }));
    }
    return FALLBACK_TASK_COLUMNS;
}

function userName(user) {
    return user?.nomeFantasia || user?.nome || user?.email || '';
}

function isDateOnly(raw, d) {
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return true;
    return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

function dueViewDate(dateString) {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    if (isDateOnly(dateString, d)) {
        return { date: new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), showTime: false, end: new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999) };
    }
    return { date: d, showTime: true, end: d };
}

function formatDue(dateString) {
    const view = dueViewDate(dateString);
    if (!view) return null;
    const d = view.date;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    if (d.toDateString() === today.toDateString()) return view.showTime ? `Hoje ${time}` : 'Hoje';
    if (d.toDateString() === tomorrow.toDateString()) return view.showTime ? `Amanhã ${time}` : 'Amanhã';
    const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
    return view.showTime ? `${date} ${time}` : date;
}

function dueTone(dateString, isDone) {
    if (!dateString || isDone) return 'ok';
    const view = dueViewDate(dateString);
    if (!view) return 'ok';
    const now = new Date();
    if (view.end < now) return 'over';
    if (view.end - now < 24 * 60 * 60 * 1000) return 'soon';
    return 'ok';
}

function stopCardEvent(e) {
    e.stopPropagation();
}

function CardMenu({ trigger, children, align = 'start' }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-no-drag
                    onClick={stopCardEvent}
                    onPointerDown={stopCardEvent}
                    className="outline-none"
                >
                    {trigger}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align={align}
                className="min-w-[160px] max-h-64 overflow-y-auto"
                onClick={stopCardEvent}
            >
                {children}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function TarefaCard({
    task,
    columns,
    lastColumnId,
    usuariosList = [],
    dragging = false,
    draggable = false,
    showLead = true,
    onDragStart,
    onDragEnd,
    onEdit,
    onStatusChange,
    onUpdate,
}) {
    const assignedId = task.assigned_to?._id || task.assigned_to || '';
    const listUser = usuariosList.find((u) => u._id === assignedId);
    const assignedName = userName(task.assigned_to) || userName(listUser) || null;
    const leadName = showLead ? task.lead_id?.nome : null;
    const priority = D.priority[task.priority] || D.priority.medium;
    const isDone = task.status === lastColumnId;
    const tone = dueTone(task.due_date, isDone);
    const dueLabel = formatDue(task.due_date);
    const checklist = task.checklist || [];
    const checklistDone = checklist.filter((c) => c.done).length;
    const currentStatus = columns.find((c) => c.id === task.status) || { id: task.status, label: task.status, cor: '#9ca3af' };

    return (
        <div
            draggable={draggable}
            onDragStart={draggable ? (e) => onDragStart?.(e, task) : undefined}
            onDragEnd={draggable ? onDragEnd : undefined}
            onClick={() => onEdit?.(task)}
            className={`${D.kanbanCard.container} ${draggable ? D.kanbanCard.containerDrag : D.kanbanCard.containerClick} ${
                dragging ? D.kanbanCard.containerDragging : ''
            }`}
        >
            {task.card_color && (
                <div className="-mx-2 -mt-2 mb-2 h-1 rounded-t-lg" style={{ backgroundColor: task.card_color }} />
            )}

            <div className={D.kanbanCard.header}>
                <CardMenu
                    trigger={
                        assignedName ? (
                            <span
                                className={`${D.kanbanCard.avatar} hover:ring-2 hover:ring-[#7C3AED]/30`}
                                style={{ backgroundColor: getCorAvatar(assignedName) }}
                                title={`Responsável: ${assignedName}`}
                            >
                                {getIniciais(assignedName)}
                            </span>
                        ) : (
                            <span className={D.kanbanCard.avatarEmpty} title="Atribuir responsável">
                                <Iconify icon="mdi:account-plus-outline" width={12} />
                            </span>
                        )
                    }
                >
                    <DropdownMenuItem
                        onSelect={() => onUpdate?.(task._id, { assigned_to: null })}
                        className="text-xs gap-2"
                    >
                        <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-[#383838] flex items-center justify-center">
                            <Iconify icon="mdi:account-off-outline" width={12} className="text-gray-400" />
                        </span>
                        Sem responsável
                    </DropdownMenuItem>
                    {usuariosList.map((u) => {
                        const nome = userName(u) || u._id;
                        const selected = u._id === assignedId;
                        return (
                            <DropdownMenuItem
                                key={u._id}
                                onSelect={() => onUpdate?.(task._id, { assigned_to: u._id })}
                                className="text-xs gap-2"
                            >
                                <span
                                    className={D.kanbanCard.avatar}
                                    style={{ backgroundColor: getCorAvatar(nome) }}
                                >
                                    {getIniciais(nome)}
                                </span>
                                <span className="truncate flex-1">{nome}</span>
                                {selected && <Iconify icon="mdi:check" width={14} className="text-[#7C3AED]" />}
                            </DropdownMenuItem>
                        );
                    })}
                </CardMenu>

                <div className="min-w-0 flex-1">
                    <p className={D.kanbanCard.title}>{task.title}</p>
                    {leadName && (
                        <p className={D.kanbanCard.subtitle}>
                            <Iconify icon="mdi:handshake-outline" width={10} className="flex-shrink-0" />
                            <span className="truncate">{leadName}</span>
                        </p>
                    )}
                    {dueLabel ? (
                        <p
                            className={`${D.kanbanCard.dueRow} ${
                                tone === 'over' ? D.kanbanCard.dueOver : tone === 'soon' ? D.kanbanCard.dueSoon : D.kanbanCard.dueOk
                            }`}
                            title="Data limite"
                        >
                            <Iconify icon="mdi:calendar-clock-outline" width={12} className="flex-shrink-0" />
                            <span className="truncate">{dueLabel}</span>
                        </p>
                    ) : (
                        <p className={D.kanbanCard.dueMissing} title="Sem data limite">
                            <Iconify icon="mdi:calendar-blank-outline" width={12} className="flex-shrink-0" />
                            Sem prazo
                        </p>
                    )}
                </div>

                <button
                    type="button"
                    data-no-drag
                    title="Abrir tarefa"
                    onClick={(e) => { e.stopPropagation(); onEdit?.(task); }}
                    onPointerDown={stopCardEvent}
                    className={D.kanbanCard.action}
                >
                    <Iconify icon="eva:edit-2-outline" width={12} />
                </button>
            </div>

            <div className={D.kanbanCard.footer}>
                <div className={D.kanbanCard.chips}>
                    <CardMenu
                        trigger={
                            <span className={D.kanbanCard.chip} style={{ color: priority.color }} title="Prioridade">
                                <Iconify icon={priority.icon} width={11} />
                                {priority.label}
                                <Iconify icon="mdi:chevron-down" width={10} className="opacity-50" />
                            </span>
                        }
                    >
                        {PRIORITY_ORDER.map((key) => {
                            const item = D.priority[key];
                            return (
                                <DropdownMenuItem
                                    key={key}
                                    onSelect={() => onUpdate?.(task._id, { priority: key })}
                                    className="text-xs gap-2"
                                >
                                    <Iconify icon={item.icon} width={14} style={{ color: item.color }} />
                                    <span className="flex-1">{item.label}</span>
                                    {task.priority === key && <Iconify icon="mdi:check" width={14} className="text-[#7C3AED]" />}
                                </DropdownMenuItem>
                            );
                        })}
                    </CardMenu>

                    <CardMenu
                        trigger={
                            <span className={D.kanbanCard.chip} title="Status">
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: currentStatus.cor }} />
                                <span className="truncate">{currentStatus.label}</span>
                                <Iconify icon="mdi:chevron-down" width={10} className="opacity-50 flex-shrink-0" />
                            </span>
                        }
                    >
                        {columns.map((col) => (
                            <DropdownMenuItem
                                key={col.id}
                                onSelect={() => {
                                    if (col.id !== task.status) onStatusChange?.(task._id, col.id);
                                }}
                                className="text-xs gap-2"
                            >
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: col.cor }} />
                                <span className="flex-1 truncate">{col.label}</span>
                                {col.id === task.status && <Iconify icon="mdi:check" width={14} className="text-[#7C3AED]" />}
                            </DropdownMenuItem>
                        ))}
                    </CardMenu>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    {checklist.length > 0 && (
                        <span className="text-[9px] font-semibold text-gray-400 tabular-nums flex items-center gap-0.5">
                            <Iconify icon="mdi:checkbox-marked-outline" width={10} />
                            {checklistDone}/{checklist.length}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
